<template>
    <div class="coaches-page">

        <!-- HERO -->
        <div class="coaches-hero">
            <brand-backdrop adaptive />
            <div class="hero-text">
                <h2>{{ $t('student.coach_title') }}</h2>
            </div>
        </div>

        <!-- SEARCH -->
        <div class="search-wrap">
            <kimsa-text-field class="search-field" ref="search" @change="search = $event" :value="search"
                :placeholder="$t('student.search_coach')" prepend-inner-icon="mdi-magnify" dense />
        </div>

        <div class="section-title">{{ $tc('coach.coach', 2) }}</div>

        <!-- LOADING -->
        <div v-if="loading" class="list-wrap">
            <skeleton-loader :type="isMobile ? 'coach-list' : 'coach-cards'" />
        </div>

        <!-- EMPTY -->
        <div v-else-if="coaches.length === 0" class="empty-state">
            <v-icon size="34" color="txtMuted">mdi-magnify</v-icon>
            <div class="empty-title">{{ $t('coach.no_coaches_found') }}</div>
            <div class="empty-text">{{ $t('common.try_another_filter') }}</div>
            <v-btn v-if="hasFilters" text small class="empty-btn" @click="clearFilters">
                {{ $t('common.clear_filters') }}
            </v-btn>
        </div>

        <template v-else>
            <!-- LIST MOBILE -->
            <div v-if="isMobile" class="coach-list">
                <coach-row v-for="coach in coaches" :key="coach.objectID" :coach="coach" />
            </div>

            <!-- GRID DESKTOP -->
            <div v-else class="coach-grid">
                <coach-card v-for="coach in coaches" :key="coach.objectID" :coach="coach" />
            </div>
        </template>

        <div class="pagination-wrap">
            <kimsa-pagination
                @page="onPage"
                :value="page + 1"
                :pageCount="pageCount"
                :total-visible="isMobile ? 5 : 7"
                center
            />
        </div>

    </div>
</template>

<script>

import { UserIndex } from '@/utils/Algolia'
import { debounce } from '@/utils/Common'

import { mapState, mapMutations } from 'vuex'
import CoachCard from "@/components/coach/CoachCard";
import CoachRow from "@/components/student/CoachRow";
import BrandBackdrop from "@/components/common/BrandBackdrop";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaPagination from "@/components/common/KimsaPagination";

const DEFAULT_FILTER = '(accountType:coach AND readyToTrain=1) '

export default {
    components: { KimsaPagination, KimsaTextField, SkeletonLoader, BrandBackdrop, CoachRow, CoachCard },
    data() {
        return {
            loading: false,
            coaches: [],
            search: '',
            page: 0,
            pageCount: 0,
        }
    },
    computed: {
        ...mapState(['filterCoach']),
        isProd() {
            return process.env.NODE_ENV === 'production'
        },
        hasFilters(vm = this) {
            return !!vm.search || vm.filterCoach !== DEFAULT_FILTER
        },
    },
    created() {
        this.debouncedLoadCoaches = debounce(this.loadCoaches, 350)
    },
    mounted() {
        this.loadCoaches()
    },
    methods: {
        ...mapMutations(['setFilterCoach']),
        async loadCoaches() {
            const vm = this
            vm.loading = true
            try {
                let appTeamQuery = '(appTeam!=1) AND '
                let response = await UserIndex.search(vm.search, {
                    filters: `${vm.isProd ? appTeamQuery : ''}${vm.filterCoach}`,
                    hitsPerPage: 12,
                    page: vm.page
                })
                vm.pageCount = response.nbPages || 0
                vm.coaches = response.hits
            } catch (e) {
                console.log('loadCoaches error', e)
            } finally {
                vm.loading = false
            }
        },
        onPage(p) {
            this.page = p - 1
            this.loadCoaches()
        },
        clearFilters() {
            const vm = this
            vm.$refs.search.reset()
            vm.search = ''
            vm.page = 0
            vm.setFilterCoach()
        },
    },
    watch: {
        filterCoach() {
            this.page = 0
            this.loadCoaches()
        },
        search() {
            this.page = 0
            this.debouncedLoadCoaches()
        },
    },
    beforeRouteLeave(to, from, next) {
        const vm = this
        vm.setFilterCoach()
        next()
    }
}
</script>

<style scoped>
.coaches-page {
    padding-bottom: 24px;
}

.coaches-hero {
    position: relative;
    height: 140px;
    overflow: hidden;
    display: flex;
    align-items: center;
}

.hero-text {
    position: relative;
    padding: 0 20px;
}

.hero-text h2 {
    font-size: 22px;
    font-weight: 800;
    line-height: 1.2;
    color: #F2F5FA;
    max-width: 260px;
    text-shadow: 0 2px 16px rgba(0, 0, 0, 0.6);
}

.theme--light .hero-text h2 {
    color: #0F1623;
    text-shadow: 0 2px 16px rgba(255, 255, 255, 0.7);
}

.search-wrap {
    padding: 14px 16px 10px;
}

.section-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    padding: 4px 16px 10px;
}

.list-wrap {
    padding: 0 16px;
}

.coach-list {
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--v-outline-base);
}

.coach-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 12px;
    padding: 0 16px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 48px 16px;
    text-align: center;
}

.empty-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    margin-top: 8px;
}

.empty-text {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.empty-btn {
    margin-top: 10px;
    color: var(--v-accent-base);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}

.theme--dark .empty-btn {
    color: var(--v-secondary-base);
}

.pagination-wrap {
    padding: 20px 16px 0;
}

@media (min-width: 960px) {
    .coaches-hero {
        height: 180px;
    }

    .hero-text {
        padding: 0 28px;
    }

    .hero-text h2 {
        font-size: 28px;
        max-width: 420px;
    }

    .search-wrap {
        padding: 16px 28px 10px;
    }

    .section-title {
        padding: 4px 28px 10px;
    }

    .coach-grid,
    .list-wrap,
    .pagination-wrap {
        padding-left: 28px;
        padding-right: 28px;
    }
}
</style>
